<template>
  <div>
    <h5 class="f-110 tail-line-dark">웹 보고서 내부 링크 설정</h5>

    <div v-if="sourceContext" class="source-context-banner mb-3">
      <b-icon icon="geo-alt-fill" class="mr-2" />
      <span class="fw-500">시작점:</span>
      <span class="ml-2">{{ sourceContext.section }}</span>
      <span v-if="sourceContext.item_label" class="ml-1 gray-700">
        &gt; "{{ sourceContext.item_label }}"
      </span>
    </div>

    <b-row>
      <b-col cols="12" lg="4">
        <h6 class="gray-700 fw-500 mb-2"><b-icon icon="list-ul" /> 목차에서 도착점 선택</h6>
        <div class="accordion accordion-scroll" role="tablist">
          <!-- 레벨 1 반복 -->
          <b-card v-for="(lv1_info, lv1_idx) in sb_toc" :key="lv1_info.key || lv1_idx" no-body>
            <b-card-header header-tag="header" class="p-1 bg-white" role="tab">
              <b-button
                v-b-toggle="'accordion-1-' + lv1_idx"
                block
                squared
                variant="icon-txt"
                class="text-left d-flex justify-content-between align-items-center"
              >
                <span style="white-space: pre-line">{{
                  isEng ? lv1_info.name_ea || lv1_info.name : lv1_info.name
                }}</span>
                <b-icon
                  class="gray-500"
                  :icon="activeAccordionLv1 === lv1_idx ? 'chevron-up' : 'chevron-down'"
                />
              </b-button>
            </b-card-header>

            <b-collapse
              :id="'accordion-1-' + lv1_idx"
              :visible="isLv1Selected(lv1_idx)"
              :accordion="'my-accordion-lv1'"
              role="tabpanel"
              @show="activeAccordionLv1 = lv1_idx"
              @hide="activeAccordionLv1 = null"
            >
              <b-card-body class="p-0">
                <div class="accordion" role="tablist">
                  <!-- 레벨 2 반복 -->
                  <b-card
                    v-for="(lv2_info, lv2_idx) in lv1_info.children"
                    :key="lv2_info.key || `${lv1_idx}-${lv2_idx}`"
                    no-body
                    class="border-radius-0"
                  >
                    <b-card-header
                      v-if="
                        lv2_info.appendix_type === null ||
                        report_types.includes(lv2_info.appendix_type)
                      "
                      header-tag="header"
                      class="d-flex align-items-center p-0"
                      role="tab"
                      style="white-space: pre-line"
                    >
                      <b-form-radio
                        v-model="selectedValue"
                        :value="`lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}`"
                        class="ml-2"
                      />
                      <b-button
                        v-b-toggle="'accordion-2-' + lv1_idx + '-' + lv2_idx"
                        block
                        squared
                        variant="icon-txt"
                        class="text-left d-flex justify-content-between align-items-center"
                      >
                        <span>{{ isEng ? lv2_info.name_ea || lv2_info.name : lv2_info.name }}</span>
                        <b-icon
                          :icon="activeAccordionLv2 === lv2_idx ? 'chevron-up' : 'chevron-down'"
                        />
                      </b-button>
                    </b-card-header>
                    <b-collapse
                      v-if="
                        lv2_info.appendix_type === null ||
                        report_types.includes(lv2_info.appendix_type)
                      "
                      :id="'accordion-2-' + lv1_idx + '-' + lv2_idx"
                      :visible="isLv2Selected(lv1_idx, lv2_idx)"
                      :accordion="'my-accordion-lv2-' + lv1_idx"
                      role="tabpanel"
                      @show="onLv2AccordionShow(lv1_info, lv2_info, lv2_idx)"
                      @hide="activeAccordionLv2 = null"
                    >
                      <b-card-body class="bgray-200">
                        <!-- GRI 지표별 라디오 -->
                        <template v-if="lv2_info.appendix_type === 'GRI'">
                          <div
                            v-if="appendixFetchState.GRI === 'error'"
                            class="text-center text-danger p-2 f-90"
                          >
                            <b-icon icon="exclamation-triangle" /> GRI 로딩 실패
                            <b-button size="sm" variant="link" @click="loadAppendix('GRI')">
                              재시도
                            </b-button>
                          </div>
                          <template v-else-if="griDisclosuresList.length > 0">
                            <b-form-radio
                              v-for="disc in griDisclosuresList"
                              :key="disc.gri_code"
                              v-model="selectedValue"
                              :value="`lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}#w${disc.gri_code}`"
                              class="ml-4 mt-1"
                            >
                              <span class="fw-500">{{ disc.gri_code }}</span>
                              <span class="ml-2 gray-700">
                                {{
                                  isEng
                                    ? disc.subtopic_name_e || disc.subtopic_name
                                    : disc.subtopic_name
                                }}
                              </span>
                            </b-form-radio>
                          </template>
                          <div
                            v-else-if="appendixFetchState.GRI === 'loaded'"
                            class="text-center text-muted p-2 f-90"
                          >
                            <b-icon icon="info-circle" /> 등록된 GRI 지표가 없습니다.
                          </div>
                          <div v-else class="text-center text-muted p-2 f-90">
                            <b-spinner small /> GRI 지표 로딩 중...
                          </div>
                        </template>

                        <!-- SASB 지표별 라디오 -->
                        <template v-else-if="lv2_info.appendix_type === 'SASB'">
                          <div
                            v-if="appendixFetchState.SASB === 'error'"
                            class="text-center text-danger p-2 f-90"
                          >
                            <b-icon icon="exclamation-triangle" /> SASB 로딩 실패
                            <b-button size="sm" variant="link" @click="loadAppendix('SASB')">
                              재시도
                            </b-button>
                          </div>
                          <template v-else-if="sasbDisclosuresList.length > 0">
                            <b-form-radio
                              v-for="disc in sasbDisclosuresList"
                              :key="disc.indicator_code"
                              v-model="selectedValue"
                              :value="`lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}#w${disc.indicator_code}`"
                              class="ml-4 mt-1"
                            >
                              <span class="fw-500">{{ disc.indicator_code }}</span>
                              <span class="ml-2 gray-700 f-90">
                                {{ isEng ? disc.topic_name_e || disc.topic_name : disc.topic_name }}
                              </span>
                            </b-form-radio>
                          </template>
                          <div
                            v-else-if="appendixFetchState.SASB === 'loaded'"
                            class="text-center text-muted p-2 f-90"
                          >
                            <b-icon icon="info-circle" /> 등록된 SASB 지표가 없습니다.
                          </div>
                          <div v-else class="text-center text-muted p-2 f-90">
                            <b-spinner small /> SASB 지표 로딩 중...
                          </div>
                        </template>

                        <!-- TCFD 지표별 라디오 -->
                        <template v-else-if="lv2_info.appendix_type === 'TCFD'">
                          <div
                            v-if="appendixFetchState.TCFD === 'error'"
                            class="text-center text-danger p-2 f-90"
                          >
                            <b-icon icon="exclamation-triangle" /> TCFD 로딩 실패
                            <b-button size="sm" variant="link" @click="loadAppendix('TCFD')">
                              재시도
                            </b-button>
                          </div>
                          <template v-else-if="tcfdDisclosuresList.length > 0">
                            <b-form-radio
                              v-for="disc in tcfdDisclosuresList"
                              :key="disc.tcfd_code"
                              v-model="selectedValue"
                              :value="`lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}#w${disc.tcfd_code}`"
                              class="ml-4 mt-1"
                            >
                              <span class="fw-500">{{ disc.tcfd_code }}</span>
                              <span class="ml-2 gray-700 f-90">
                                {{
                                  isEng
                                    ? disc.category_name_e || disc.category_name
                                    : disc.category_name
                                }}
                              </span>
                            </b-form-radio>
                          </template>
                          <div
                            v-else-if="appendixFetchState.TCFD === 'loaded'"
                            class="text-center text-muted p-2 f-90"
                          >
                            <b-icon icon="info-circle" /> 등록된 TCFD 지표가 없습니다.
                          </div>
                          <div v-else class="text-center text-muted p-2 f-90">
                            <b-spinner small /> TCFD 지표 로딩 중...
                          </div>
                        </template>

                        <!-- 일반 lv2: 모든 disclosure 요소 (제목 + 텍스트/이미지/차트/표 등) -->
                        <template v-else>
                          <div
                            v-if="!isLv2DisclosuresLoaded(lv1_info, lv2_info)"
                            class="text-center text-muted p-2 f-90"
                          >
                            <b-spinner small /> 컴포넌트 로딩 중...
                          </div>
                          <template v-else>
                            <b-form-radio
                              v-for="(disc, disc_idx) in lv2DisclosuresList(lv1_info, lv2_info)"
                              :key="disc.id || disc.link || `${lv1_idx}-${lv2_idx}-${disc_idx}`"
                              v-model="selectedValue"
                              :value="discRadioValue(lv1_idx, lv2_idx, disc)"
                              :class="discRadioClass(disc, disc_idx)"
                            >
                              {{ discDisplayLabel(disc) }}
                            </b-form-radio>
                          </template>
                        </template>
                      </b-card-body>
                    </b-collapse>
                  </b-card>
                </div>
              </b-card-body>
            </b-collapse>
          </b-card>
        </div>
      </b-col>

      <b-col cols="12" lg="8">
        <h6 class="gray-700 fw-500 mb-2"><b-icon icon="eye" /> 도착점 미리보기</h6>
        <div ref="previewPane" class="preview-pane">
          <div v-if="!selectedValue" class="preview-empty">
            <b-icon icon="arrow-left-circle" font-scale="2" class="mb-2" />
            <span>좌측에서 항목을 선택하면<br />도착점 위치가 미리보기됩니다.</span>
          </div>

          <!-- GRI 미리보기 -->
          <template v-else-if="appendixType === 'GRI'">
            <div v-if="appendixFetchState.GRI === 'error'" class="preview-empty">
              <b-icon icon="exclamation-triangle" font-scale="2" class="mb-2 text-danger" />
              <span>GRI 미리보기 로딩 실패</span>
              <b-button size="sm" variant="link" @click="loadAppendix('GRI')">재시도</b-button>
            </div>
            <div v-else-if="appendixLoaded" class="appendix-preview">
              <GRITable2021 :modal_table="true" />
            </div>
            <div v-else-if="appendixFetchState.GRI === 'loaded'" class="preview-empty">
              <b-icon icon="info-circle" font-scale="2" class="mb-2" />
              <span>등록된 GRI 지표가 없습니다.</span>
            </div>
            <div v-else class="preview-empty">
              <b-spinner small variant="secondary" class="mb-2" />
              <span>GRI Index 미리보기 로딩 중...</span>
            </div>
          </template>

          <!-- SASB 미리보기 -->
          <template v-else-if="appendixType === 'SASB'">
            <div v-if="appendixFetchState.SASB === 'error'" class="preview-empty">
              <b-icon icon="exclamation-triangle" font-scale="2" class="mb-2 text-danger" />
              <span>SASB 미리보기 로딩 실패</span>
              <b-button size="sm" variant="link" @click="loadAppendix('SASB')">재시도</b-button>
            </div>
            <div v-else-if="appendixLoaded" class="appendix-preview">
              <SASBTable :modal_table="true" />
            </div>
            <div v-else-if="appendixFetchState.SASB === 'loaded'" class="preview-empty">
              <b-icon icon="info-circle" font-scale="2" class="mb-2" />
              <span>등록된 SASB 지표가 없습니다.</span>
            </div>
            <div v-else class="preview-empty">
              <b-spinner small variant="secondary" class="mb-2" />
              <span>SASB 미리보기 로딩 중...</span>
            </div>
          </template>

          <!-- TCFD 미리보기 -->
          <template v-else-if="appendixType === 'TCFD'">
            <div v-if="appendixFetchState.TCFD === 'error'" class="preview-empty">
              <b-icon icon="exclamation-triangle" font-scale="2" class="mb-2 text-danger" />
              <span>TCFD 미리보기 로딩 실패</span>
              <b-button size="sm" variant="link" @click="loadAppendix('TCFD')">재시도</b-button>
            </div>
            <div v-else-if="appendixLoaded" class="appendix-preview">
              <TCFDTable :modal_table="true" />
            </div>
            <div v-else-if="appendixFetchState.TCFD === 'loaded'" class="preview-empty">
              <b-icon icon="info-circle" font-scale="2" class="mb-2" />
              <span>등록된 TCFD 지표가 없습니다.</span>
            </div>
            <div v-else class="preview-empty">
              <b-spinner small variant="secondary" class="mb-2" />
              <span>TCFD 미리보기 로딩 중...</span>
            </div>
          </template>

          <!-- 데이터센터/검증의견서: PR 5에서 처리 예정 -->
          <div
            v-else-if="appendixType === 'DATACENTER' || appendixType === 'ASSURANCE'"
            class="preview-empty"
          >
            <b-icon icon="info-circle" font-scale="2" class="mb-2" />
            <span
              ><strong>{{ appendixTypeLabel }}</strong> 전용 미리보기는<br />다음 업데이트에서
              지원됩니다.</span
            >
          </div>

          <!-- 일반 스토리보드 미리보기 -->
          <div v-else-if="previewDisclosures === undefined" class="preview-empty">
            <b-spinner small variant="secondary" class="mb-2" />
            <span>미리보기 로딩 중...</span>
          </div>
          <div v-else-if="previewDisclosures.length === 0" class="preview-empty">
            <b-icon icon="file-earmark" font-scale="2" class="mb-2" />
            <span>해당 페이지에 내용이 없습니다.</span>
          </div>
          <Content
            v-else
            :disclosures="previewDisclosures"
            :lv2_key="previewLv2Key + '_preview'"
            :print_mode="false"
          />
        </div>
      </b-col>
    </b-row>

    <div class="pt-3 pb-1">
      <p class="fw-400 f-90 mb-2">URL 이름 설정</p>
      <b-form-input
        v-model="local_link.title"
        placeholder="URL 명"
        class="mb-3"
        @input="autoUpdateTitle = false"
      />

      <p class="fw-400 f-90 mb-2">
        링크 보기<br />
        <span class="red">
          <b-icon icon="exclamation-circle" />
          보고서를 공개했을 때 유효한 주소입니다.
        </span>
      </p>
      <b-form-textarea
        v-model="local_link.link"
        :disabled="true"
        placeholder=""
        rows="3"
        class="mb-2"
      />
    </div>

    <div class="pt-1 pb-2 d-flex justify-content-end">
      <b-button variant="teal" class="mr-2" @click="confirmSelection">확인</b-button>
      <b-button variant="secondary" @click="$emit('close')">취소</b-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Content from '@/components/report_preview/story_board/viewer_component/Content';
import GRITable2021 from '@/components/report_preview/gri2021_viewer/viewer_components/GRITable2021.vue';
import SASBTable from '@/components/report_preview/gri2021_viewer/viewer_components/SASBTable.vue';
import TCFDTable from '@/components/report_preview/gri2021_viewer/viewer_components/TCFDTable.vue';
import { getViewerBaseUrl } from '@/utils/url';
import { releaseBodyScrollLockIfStandalone } from '@/utils/modal-scroll';

const APPENDIX_LABELS = {
  GRI: 'GRI Index',
  SASB: 'SASB',
  TCFD: 'TCFD',
  DATACENTER: '데이터 센터',
  ASSURANCE: '검증 의견서'
};

export default {
  name: 'InternalLinkSelector',
  components: { Content, GRITable2021, SASBTable, TCFDTable },
  props: {
    selectedUrl: {
      type: String,
      default: ''
    },
    selectedTitle: {
      type: String,
      default: ''
    },
    sourceContext: {
      type: Object,
      default: null
      // shape: { section: String, item_label: String }
    }
  },
  data() {
    return {
      selectedValue: null,
      activeAccordionLv1: null,
      activeAccordionLv2: null,
      local_link: {
        title: this.selectedTitle || '',
        link: this.selectedUrl || ''
      },
      // selectedTitle이 외부(드래그한 텍스트 등)에서 들어왔거나 사용자가 직접 입력한 경우
      // false → 라디오 변경 시 자동으로 'lv1 > lv2 > ...' 로 덮어쓰지 않음
      autoUpdateTitle: !this.selectedTitle,
      // 'idle' | 'loading' | 'loaded' | 'error'
      appendixFetchState: { GRI: 'idle', SASB: 'idle', TCFD: 'idle' },
      appendixFetchError: { GRI: null, SASB: null, TCFD: null }
    };
  },
  computed: {
    ...mapGetters('story_board', ['sb_toc']),
    ...mapState('story_board', ['viewer_disclosure_map']),
    ...mapState('report_contents', ['sasb_contents', 'tcfd_contents']),
    ...mapState('gri2021_report_view', ['report_structure']),
    ...mapState('report_list', ['report_code', 'lang']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapGetters('assurance', ['latest_assurance']),

    isEng() {
      return this.lang === 'ENG';
    },

    report_types() {
      const user_selected_report_type = Object.keys(this.selected_report.report_type).map(
        item => item.split('v')[0]
      );
      if (this.latest_assurance) {
        user_selected_report_type.push('ASSURANCE');
      }

      if (this.selected_report?.data_center) {
        user_selected_report_type.push('DATACENTER');
      }
      return user_selected_report_type;
    },

    previewState() {
      if (!this.selectedValue) return null;
      const [queryPart, hashPart] = this.selectedValue.split('#');
      // prefix: 'title_' (제목), 'sr_content_' (일반 컴포넌트), 'w' (GRI/SASB/TCFD 지표)
      const lv3_match = hashPart?.match(/^(sr_content_|title_|w)(.+)/);
      const lv3_prefix = lv3_match ? lv3_match[1] : null;
      const lv3_link = lv3_match ? lv3_match[2] : null;
      const lv1_match = queryPart.match(/lv1Idx=(\d+)/);
      const lv2_match = queryPart.match(/lv2Idx=(\d+)/);
      const lv1_idx = lv1_match ? Number(lv1_match[1]) : -1;
      const lv2_idx = lv2_match ? Number(lv2_match[1]) : -1;

      if (lv1_idx < 0) return null;

      const lv1Info = this.sb_toc[lv1_idx] || null;
      if (!lv1Info) return null;
      const lv2Info = lv2_idx >= 0 ? lv1Info.children?.[lv2_idx] || null : null;

      return { lv1Info, lv2Info, lv3Link: lv3_link, lv3Prefix: lv3_prefix };
    },

    griDisclosuresList() {
      if (!this.report_structure || this.report_structure.length === 0) return [];
      const seen = new Set();
      return this.report_structure.reduce((arr, item) => {
        if (item.gri_code && !seen.has(item.gri_code)) {
          seen.add(item.gri_code);
          arr.push({
            gri_code: item.gri_code,
            subtopic_name: item.subtopic_name || '',
            subtopic_name_e: item.subtopic_name_e || '',
            topic_name: item.topic_name || '',
            category_name: item.category_name || ''
          });
        }
        return arr;
      }, []);
    },

    sasbDisclosuresList() {
      if (!this.sasb_contents || Object.keys(this.sasb_contents).length === 0) return [];
      const seen = new Set();
      const arr = [];
      Object.keys(this.sasb_contents).forEach(industryKey => {
        const industry = this.sasb_contents[industryKey] || {};
        const metrics = industry.sustainability_metrics || {};
        Object.keys(metrics).forEach(topicKey => {
          const topic = metrics[topicKey] || {};
          const children = topic.children || [];
          children.forEach(disc => {
            if (disc.indicator_code && !seen.has(disc.indicator_code)) {
              seen.add(disc.indicator_code);
              arr.push({
                indicator_code: disc.indicator_code,
                metric: disc.metric || '',
                metric_e: disc.metric_e || '',
                topic_name: topic.topic_name || '',
                topic_name_e: topic.topic_name_e || '',
                industry_name: industry.industry_name || '',
                industry_name_e: industry.industry_name_e || ''
              });
            }
          });
        });
      });
      return arr;
    },

    tcfdDisclosuresList() {
      if (!this.tcfd_contents || this.tcfd_contents.length === 0) return [];
      const seen = new Set();
      const arr = [];
      this.tcfd_contents.forEach(category => {
        const indicators = (category && category.indicators) || [];
        indicators.forEach(disc => {
          if (disc.tcfd_code && !seen.has(disc.tcfd_code)) {
            seen.add(disc.tcfd_code);
            arr.push({
              tcfd_code: disc.tcfd_code,
              indicator_name: disc.indicator_name || '',
              indicator_name_e: disc.indicator_name_e || '',
              topic: disc.topic || '',
              category_name: category.category_name || '',
              category_name_e: category.category_name_e || ''
            });
          }
        });
      });
      return arr;
    },

    appendixType() {
      return this.previewState?.lv2Info?.appendix_type || null;
    },

    appendixTypeLabel() {
      return APPENDIX_LABELS[this.appendixType] || this.appendixType || '';
    },

    appendixLoaded() {
      if (this.appendixType === 'SASB') {
        return Object.keys(this.sasb_contents || {}).length > 0;
      }
      if (this.appendixType === 'TCFD') {
        return (this.tcfd_contents || []).length > 0;
      }
      if (this.appendixType === 'GRI') {
        return !!this.report_structure;
      }
      return false;
    },

    previewLv1Key() {
      return this.previewState?.lv1Info?.key || '';
    },

    previewLv2Key() {
      return this.previewState?.lv2Info?.key || '';
    },

    previewDisclosures() {
      if (!this.previewLv1Key || !this.previewLv2Key) return undefined;
      if (this.appendixType) return undefined;
      return this.viewer_disclosure_map?.[this.previewLv1Key]?.[this.previewLv2Key];
    },

    viewerBaseUrl() {
      return getViewerBaseUrl();
    }
  },
  watch: {
    selectedUrl(newUrl) {
      // 부모가 다른 항목을 편집하기 위해 prop을 변경한 경우 selectedValue 재설정
      const parsed = newUrl ? this.parseUrlToSelectedValue(newUrl) : null;
      if (parsed !== this.selectedValue) {
        this.selectedValue = parsed;
      }
    },
    previewState: {
      immediate: true,
      handler(newV) {
        if (!newV || !newV.lv2Info) return;

        const appendix = newV.lv2Info.appendix_type;
        if (appendix) {
          this.ensureAppendixLoaded(appendix);
          // 데이터가 이미 로드된 경우 즉시 스크롤. scrollToTarget 자체에 15회 retry가
          // 있으므로 외부에서 setTimeout 으로 지연시킬 필요가 없음.
          if (this.appendixLoaded) {
            this.$nextTick(() => this.scrollToTarget());
          }
          return;
        }

        const lv1Key = newV.lv1Info.key;
        const lv2Key = newV.lv2Info.key;
        const cached = this.viewer_disclosure_map?.[lv1Key]?.[lv2Key];
        if (cached === undefined) {
          this.$store.dispatch('story_board/fetch_viewer_sb_disclosures', {
            report_code: this.report_code,
            selected_lv_info: { lv1_info: newV.lv1Info, lv2_info: newV.lv2Info }
          });
        } else {
          this.$nextTick(() => this.scrollToTarget());
        }
      }
    },
    previewDisclosures(newV) {
      if (newV !== undefined && this.previewState?.lv3Link) {
        this.$nextTick(() => this.scrollToTarget());
      }
    },
    appendixLoaded(newV) {
      if (newV && this.previewState?.lv3Link) {
        this.$nextTick(() => this.scrollToTarget());
      }
    },
    selectedValue: {
      immediate: true,
      handler(newV) {
        if (!newV) {
          if (this.autoUpdateTitle) {
            this.local_link.title = null;
          }
          this.local_link.link = null;
          return;
        }

        try {
          // 해시 분리: 'title_' / 'sr_content_' / 'w' prefix 지원
          const [queryPart, hashPart] = newV.split('#');
          const lv3_match = hashPart?.match(/^(sr_content_|title_|w)(.+)/);
          const lv3_prefix = lv3_match ? lv3_match[1] : null;
          const lv3_key = lv3_match ? lv3_match[2] : null;

          // 라디오 값에 담긴 lv1/lv2 인덱스로 sb_toc 직접 조회 (동명 lv1 회피)
          const lv1_idx_match = queryPart.match(/lv1Idx=(\d+)/);
          const lv2_idx_match = queryPart.match(/lv2Idx=(\d+)/);
          const lv1_idx = lv1_idx_match ? Number(lv1_idx_match[1]) : -1;
          const lv2_idx = lv2_idx_match ? Number(lv2_idx_match[1]) : -1;

          const selected_lv1_info = lv1_idx >= 0 ? this.sb_toc[lv1_idx] || null : null;
          const selected_lv2_info =
            selected_lv1_info && lv2_idx >= 0
              ? selected_lv1_info.children?.[lv2_idx] || null
              : null;

          // ✅ 사용자 정의 인코딩 함수 (한글 제외하고 인코딩)
          const customEncode = str => {
            if (!str) return null;
            return str
              .split('')
              .map(char => {
                const code = char.charCodeAt(0);
                if (code >= 0xac00 && code <= 0xd7a3) return char; // 한글 그대로
                if (char === ' ') return '+';
                return encodeURIComponent(char);
              })
              .join('');
          };

          // URL에는 실제 lv1/lv2 이름을 인코딩해서 사용 (뷰어 호환 유지)
          const lv1_key = selected_lv1_info ? customEncode(selected_lv1_info.name) : null;
          const lv2_key = selected_lv2_info ? customEncode(selected_lv2_info.name) : null;
          // 'w' prefix (지표 코드) 와 'sr_content_' prefix (컴포넌트 ID) 는 인코딩 없이 사용
          const lv3_encoded =
            lv3_prefix === 'w' || lv3_prefix === 'sr_content_' ? lv3_key : customEncode(lv3_key);

          // title 자동 세팅 (autoUpdateTitle=false 일 때는 외부에서 받은 title 보존)
          if (!lv1_key && !lv2_key && lv3_encoded) {
            if (this.autoUpdateTitle) {
              const mainKeys = [
                'web-report-title',
                ...this.selected_main_option.map((_, idx) => this.coverLv3KeyByIdx(idx))
              ];
              const mainLabels = [
                '보고서 제목',
                ...this.selected_main_option.map(opt => this.getOptionLabel(opt))
              ];
              const idx = mainKeys.findIndex(k => k === lv3_encoded);
              this.local_link.title = idx !== -1 ? `메인페이지 > ${mainLabels[idx]}` : '메인페이지';
            }
          } else {
            // Appendix 지표 링크 (GRI/SASB/TCFD): 코드로 disclosure 정보 조회
            const appendixType = selected_lv2_info?.appendix_type;
            const isAppendixIndicator =
              ['GRI', 'SASB', 'TCFD'].includes(appendixType) && lv3_prefix === 'w' && lv3_encoded;

            // lang-aware 이름 추출
            const isEngMode = this.isEng;
            const lv1DisplayName = selected_lv1_info
              ? isEngMode
                ? selected_lv1_info.name_ea || selected_lv1_info.name
                : selected_lv1_info.name
              : null;
            const lv2DisplayName = selected_lv2_info
              ? isEngMode
                ? selected_lv2_info.name_ea || selected_lv2_info.name
                : selected_lv2_info.name
              : null;

            let generatedTitle = null;
            if (isAppendixIndicator) {
              let suffix = lv3_key;
              if (appendixType === 'GRI') {
                const d = this.griDisclosuresList.find(x => x.gri_code === lv3_key);
                const nm = d
                  ? isEngMode
                    ? d.subtopic_name_e || d.subtopic_name
                    : d.subtopic_name
                  : '';
                suffix = d ? `GRI ${d.gri_code}${nm ? ' ' + nm : ''}` : `GRI ${lv3_key}`;
              } else if (appendixType === 'SASB') {
                const d = this.sasbDisclosuresList.find(x => x.indicator_code === lv3_key);
                const nm = d ? (isEngMode ? d.topic_name_e || d.topic_name : d.topic_name) : '';
                suffix = d ? `SASB ${d.indicator_code}${nm ? ' ' + nm : ''}` : `SASB ${lv3_key}`;
              } else if (appendixType === 'TCFD') {
                const d = this.tcfdDisclosuresList.find(x => x.tcfd_code === lv3_key);
                const nm = d
                  ? isEngMode
                    ? d.category_name_e || d.category_name
                    : d.category_name
                  : '';
                suffix = d ? `TCFD ${d.tcfd_code}${nm ? ' ' + nm : ''}` : `TCFD ${lv3_key}`;
              }
              generatedTitle = `${lv1DisplayName} > ${lv2DisplayName} > ${suffix}`;
            } else if (lv3_prefix === 'sr_content_' && lv3_encoded) {
              // 일반 컴포넌트 (텍스트/이미지/차트 등) — viewer_disclosure_map에서 조회
              const discList =
                this.viewer_disclosure_map?.[selected_lv1_info?.key]?.[selected_lv2_info?.key] ||
                [];
              const disc = discList.find(d => String(d.id) === String(lv3_encoded));
              const label = disc ? this.discDisplayLabel(disc) : '컴포넌트';
              generatedTitle = `${lv1DisplayName} > ${lv2DisplayName} > ${label}`;
            } else {
              const selected_lv3_info =
                selected_lv2_info && lv3_encoded
                  ? selected_lv2_info.children?.find(obj => obj.link === lv3_encoded)
                  : null;

              if (selected_lv1_info && selected_lv2_info && selected_lv3_info) {
                const lv3Title = isEngMode
                  ? selected_lv3_info.title1_em ||
                    selected_lv3_info.title1_ea ||
                    selected_lv3_info.title1
                  : selected_lv3_info.title1;
                generatedTitle = `${lv1DisplayName} > ${lv2DisplayName} > ${lv3Title}`;
              } else if (selected_lv1_info && selected_lv2_info) {
                generatedTitle = `${lv1DisplayName} > ${lv2DisplayName}`;
              } else {
                generatedTitle = lv1DisplayName || null;
              }
            }

            if (this.autoUpdateTitle) {
              this.local_link.title = generatedTitle;
            }
          }

          // 링크 생성
          const reportIdentifier = this.selected_report.report_url
            ? this.selected_report.report_url
            : this.selected_report.report_code;

          let url = `${this.viewerBaseUrl}/v?r=${reportIdentifier}`;
          if (lv1_key) url += `&lv1=${lv1_key}`;
          if (lv2_key) url += `&lv2=${lv2_key}`;
          if (lv3_encoded && lv3_prefix) {
            url += `#${lv3_prefix}${lv3_encoded}`;
          }
          this.local_link.link = url;
        } catch (err) {
          console.error('selectedValue handler error:', err);
        }
      }
    }
  },
  created() {
    // 기존 URL이 있으면 selectedValue 사전 설정 (라디오 자동 체크)
    if (this.selectedUrl) {
      this.selectedValue = this.parseUrlToSelectedValue(this.selectedUrl);
    }
  },
  beforeDestroy() {
    // 모달이 비정상적으로 닫혔을 때 화면 스크롤 잠김 failsafe
    releaseBodyScrollLockIfStandalone();
  },
  methods: {
    parseUrlToSelectedValue(url) {
      // 저장된 URL을 InternalLinkSelector의 selectedValue 포맷(idx 기반)으로 역변환
      // 입력: 'https://report.qesg.co.kr/v?r=ABC&lv1=환경&lv2=기후변화#title_xxx'
      // 출력: 'lv1Idx=0&lv2Idx=2#title_xxx'
      // 동명 lv1이 있을 경우 첫 번째 일치를 선택 (기존 동작 유지)
      if (!url || typeof url !== 'string') return null;
      const match = url.match(/^https?:\/\/[^/]+\/v\?r=[^&]+&(.+)$/);
      if (!match) return null;

      const decode = s => {
        if (!s) return null;
        try {
          return decodeURIComponent(s.replace(/\+/g, '%20'));
        } catch (e) {
          return s;
        }
      };

      const queryAndHash = match[1];
      const hashIdx = queryAndHash.indexOf('#');
      const queryPart = hashIdx >= 0 ? queryAndHash.substring(0, hashIdx) : queryAndHash;
      const hashPart = hashIdx >= 0 ? queryAndHash.substring(hashIdx + 1) : null;

      const lv1Match = queryPart.match(/lv1=([^&]*)/);
      const lv2Match = queryPart.match(/lv2=([^&]*)/);
      const lv1Name = decode(lv1Match?.[1]);
      const lv2Name = decode(lv2Match?.[1]);

      // 이름 → 인덱스 변환 (동명일 때 첫 번째 매칭)
      const lv1_idx = lv1Name ? this.sb_toc.findIndex(o => o.name === lv1Name) : -1;
      const lv2_idx =
        lv1_idx >= 0 && lv2Name
          ? (this.sb_toc[lv1_idx].children || []).findIndex(o => o.name === lv2Name)
          : -1;

      if (lv1_idx < 0) return null;

      let result = `lv1Idx=${lv1_idx}`;
      if (lv2_idx >= 0) result += `&lv2Idx=${lv2_idx}`;

      if (hashPart) {
        const hashMatch = hashPart.match(/^(sr_content_|title_|w)(.+)/);
        if (hashMatch) {
          const prefix = hashMatch[1];
          const rawKey = hashMatch[2];
          // 'title_'은 customEncode 적용된 상태로 저장 → 디코딩 필요
          // 'w', 'sr_content_'은 raw 상태 → 그대로 사용
          const decodedKey = prefix === 'title_' ? decode(rawKey) : rawKey;
          result += `#${prefix}${decodedKey}`;
        } else {
          result += `#${hashPart}`;
        }
      }

      return result || null;
    },
    onLv2AccordionShow(lv1, lv2, lv2_idx) {
      // accordion이 펼쳐질 때만 해당 lv2 데이터 fetch (mounted 시 eager fetch 제거)
      this.activeAccordionLv2 = lv2_idx;
      if (!this.report_code) return;

      const appendix = lv2.appendix_type;
      if (appendix && ['GRI', 'SASB', 'TCFD'].includes(appendix)) {
        this.ensureAppendixLoaded(appendix);
        return;
      }

      // 일반 lv2: 해당 lv1/lv2의 disclosures만 부분 fetch
      const cached = this.viewer_disclosure_map?.[lv1.key]?.[lv2.key];
      if (cached === undefined) {
        this.$store.dispatch('story_board/fetch_viewer_sb_disclosures', {
          report_code: this.report_code,
          selected_lv_info: { lv1_info: lv1, lv2_info: lv2 }
        });
      }
    },
    isLv1Selected(lv1_idx) {
      if (!this.selectedValue || lv1_idx == null) return false;
      const prefix = `lv1Idx=${lv1_idx}`;
      return (
        this.selectedValue === prefix ||
        this.selectedValue.startsWith(`${prefix}&`) ||
        this.selectedValue.startsWith(`${prefix}#`)
      );
    },
    isLv2Selected(lv1_idx, lv2_idx) {
      if (!this.selectedValue || lv1_idx == null || lv2_idx == null) return false;
      const prefix = `lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}`;
      return this.selectedValue === prefix || this.selectedValue.startsWith(`${prefix}#`);
    },
    isLv2DisclosuresLoaded(lv1, lv2) {
      return Array.isArray(this.viewer_disclosure_map?.[lv1.key]?.[lv2.key]);
    },
    lv2DisclosuresList(lv1, lv2) {
      const list = this.viewer_disclosure_map?.[lv1.key]?.[lv2.key] || [];
      // link/id 가 있는 항목만 노출 (앵커 가능한 요소)
      return list.filter(d => d && (d.link || d.id));
    },
    discRadioValue(lv1_idx, lv2_idx, disc) {
      // 제목은 #title_{link}, 그 외 컴포넌트는 #sr_content_{id}
      if (disc.type === 'title' && disc.link) {
        return `lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}#title_${disc.link}`;
      }
      return `lv1Idx=${lv1_idx}&lv2Idx=${lv2_idx}#sr_content_${disc.id}`;
    },
    discRadioClass(disc, idx) {
      const classes = [];
      if (disc.type === 'title') {
        if (disc.title_lv === 1) classes.push('ml-2');
        else if (disc.title_lv === 2) classes.push('ml-4');
        else classes.push('ml-5');
        if (idx === 0) classes.push('mt-0');
        else if (disc.title_lv === 1) classes.push('mt-2', 'pt-2', 'border-top');
        else classes.push('mt-1');
      } else {
        classes.push('ml-5', 'mt-1', 'gray-600', 'f-90');
      }
      return classes;
    },
    discDisplayLabel(disc) {
      const isEng = this.isEng;
      const TEXT_PREVIEW_LEN = 40;

      // 유효한 문자열인지 (null 문자열 / 공백 / 'null' 제외)
      const isValid = s => s != null && String(s).trim() !== '' && String(s) !== 'null';

      // 제목 (대/중/소/요소/단락 — title_lv 1~5 모두 동일하게 처리)
      if (disc.type === 'title') {
        const t = isEng ? disc.title1_em || disc.title1_ea || disc.title1 : disc.title1;
        return isValid(t) ? t : '[제목 없음]';
      }

      // 이미지 — image_title
      if (disc.type === 'image') {
        const t = isEng
          ? disc.image_title_em || disc.image_title_ea || disc.image_title
          : disc.image_title;
        return isValid(t) ? t : '[이미지]';
      }

      // 데이터 표 — data.title
      if (disc.type === 'data_table') {
        const t = isEng ? disc.data?.translated?.title_e || disc.data?.title : disc.data?.title;
        return isValid(t) ? t : '[데이터 표]';
      }

      // 비즈니스 케이스 — data.title
      if (disc.type === 'business_case') {
        const t = isEng ? disc.data?.translated?.title_e || disc.data?.title : disc.data?.title;
        return isValid(t) ? t : '[비즈니스 케이스]';
      }

      // 텍스트 — 첫 ${TEXT_PREVIEW_LEN}자
      if (disc.type === 'text' || disc.type === 'md' || disc.type === 'md_sb') {
        const raw = isEng ? disc.text_em || disc.text_ea || disc.text : disc.text;
        if (raw) {
          const stripped = String(raw)
            .replace(/<[^>]*>/g, '')
            .trim();
          if (stripped) {
            return (
              stripped.slice(0, TEXT_PREVIEW_LEN) +
              (stripped.length > TEXT_PREVIEW_LEN ? '...' : '')
            );
          }
        }
        return '[텍스트]';
      }

      // 그 외 (차트/문서 등) — 타입 라벨 fallback
      const typeLabels = {
        chart: '차트',
        chart_canvas: '차트',
        documents: '문서',
        document: '문서',
        document_set: '문서 묶음'
      };
      return `[${typeLabels[disc.type] || disc.type || '컴포넌트'}]`;
    },
    ensureAppendixLoaded(appendix) {
      if (!this.report_code) return;
      if (this.appendixFetchState[appendix] === 'loading') return;
      if (appendix === 'SASB' && Object.keys(this.sasb_contents || {}).length === 0) {
        this.loadAppendix('SASB');
      } else if (appendix === 'TCFD' && (this.tcfd_contents || []).length === 0) {
        this.loadAppendix('TCFD');
      } else if (appendix === 'GRI' && !this.report_structure) {
        this.loadAppendix('GRI');
      }
    },
    async loadAppendix(type) {
      if (!this.report_code) return;
      if (this.appendixFetchState[type] === 'loading') return;
      this.$set(this.appendixFetchState, type, 'loading');
      this.$set(this.appendixFetchError, type, null);
      try {
        if (type === 'GRI') {
          await this.$store.dispatch('gri2021_report_view/readReportContents', {
            report_code: this.report_code,
            editor_mode: false
          });
        } else if (type === 'SASB') {
          await this.$store.dispatch('report_contents/fetchSelectedSasb', this.report_code);
        } else if (type === 'TCFD') {
          await this.$store.dispatch('report_contents/fetchSelectedTcfd', this.report_code);
        }
        this.$set(this.appendixFetchState, type, 'loaded');
      } catch (err) {
        const msg = err?.message || err?.response?.statusText || '로딩 실패';
        // eslint-disable-next-line no-console
        console.warn('[InternalLinkSelector] appendix fetch error', type, err);
        this.$set(this.appendixFetchError, type, msg);
        this.$set(this.appendixFetchState, type, 'error');
      }
    },
    scrollToTarget(retryCount = 0) {
      const link = this.previewState?.lv3Link;
      const prefix = this.previewState?.lv3Prefix;
      const pane = this.$refs.previewPane;
      if (!pane) return;

      // 첫 시도에만 이전 하이라이트 제거
      if (retryCount === 0) {
        pane
          .querySelectorAll('.preview-highlight')
          .forEach(el => el.classList.remove('preview-highlight'));
      }

      if (!link || !prefix) return;

      let target = null;
      try {
        target = pane.querySelector('#' + CSS.escape(prefix + link));
      } catch (e) {
        target = null;
      }

      if (target) {
        target.classList.add('preview-highlight');
        target.scrollIntoView({ behavior: 'auto', block: 'center' });
      } else if (retryCount < 15) {
        // appendix viewer (GRI/SASB/TCFD) 비동기 렌더 대비 재시도
        setTimeout(() => {
          // 사용자가 다른 항목을 선택했으면 stale 재시도 중단
          if (this.previewState?.lv3Link === link && this.previewState?.lv3Prefix === prefix) {
            this.scrollToTarget(retryCount + 1);
          }
        }, 300);
      }
    },
    buildLinkMeta() {
      const ps = this.previewState;
      if (!ps) return null;
      const appendix = ps.lv2Info?.appendix_type;
      let category = 'sb';
      if (appendix === 'GRI') category = 'gri';
      else if (appendix === 'SASB') category = 'sasb';
      else if (appendix === 'TCFD') category = 'tcfd';
      else if (appendix === 'DATACENTER') category = 'datacenter';
      else if (appendix === 'ASSURANCE') category = 'assurance';

      const anchor = ps.lv3Prefix && ps.lv3Link ? `${ps.lv3Prefix}${ps.lv3Link}` : null;

      return {
        category,
        lv1_key: ps.lv1Info?.key || null,
        lv2_key: ps.lv2Info?.key || null,
        anchor
      };
    },
    confirmSelection() {
      if (this.selectedValue) {
        if (!this.local_link.title) {
          alert('URL 이름을 설정해주세요.');
          return;
        }
        // 선택된 링크 데이터 전달
        this.$emit('link-selected', {
          link: this.local_link.link,
          title: this.local_link.title,
          link_meta: this.buildLinkMeta()
        });
        this.$emit('close');
      } else {
        alert('연결할 목차를 선택해주세요.');
      }
    }
  }
};
</script>

<style scoped>
.accordion-scroll {
  max-height: 42vh;
  overflow-y: auto;
}
.preview-pane {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
  height: 42vh;
  overflow-y: auto;
}
.preview-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
}
.preview-pane >>> .preview-highlight {
  outline: 3px solid #ffc107;
  outline-offset: 4px;
  border-radius: 4px;
  background-color: rgba(255, 193, 7, 0.1);
  transition: outline 0.3s, background-color 0.3s;
}
.appendix-preview {
  font-size: 0.875rem;
}
.appendix-preview >>> .side-nav3 {
  display: none;
}
.source-context-banner {
  padding: 0.5rem 1rem;
  background: #e9ecef;
  border-left: 4px solid #6c757d;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #495057;
}
</style>

<style>
/* 전역 스타일 — 내부 링크 모달 너비 확장 */
.internal-link-modal-wider {
  max-width: 95vw !important;
  width: 1600px;
}
@media (max-width: 1700px) {
  .internal-link-modal-wider {
    width: 95vw;
  }
}
</style>
